/**
 * Export data builders — Admin-only.
 * Each function takes already-loaded task data and returns a CSV string.
 * Reuses the same calculation logic as the dashboard so exported values
 * always match what the Admin sees on screen.
 */

import { buildCSV, csvDateTime, csvDate } from './csv';

function taskDisplayName(task) {
  return task.isCustomTaskName && task.customTaskName
    ? task.customTaskName
    : (task.taskName || '');
}

function rateSourceLabel(src) {
  switch (src) {
    case 'individualRates': return 'Individual rates';
    case 'defaultTeamRate': return 'Default team rate';
    case 'partialRates': return 'Partial rates (incomplete — some workers missing rate)';
    case 'unavailable': return 'No rate configured';
    default: return src || '';
  }
}

/**
 * Full per-task export. Columns match the task document fields; derived
 * values (labour cost, cost/unit, etc.) come from what is stored on the task
 * record — the same values displayed in the Admin dashboard — so no
 * recalculation happens here and numbers are guaranteed to match the UI.
 */
export function buildProductionTasksCSV(tasks, currency) {
  const cur = currency || 'CAD';

  const headers = [
    'Task ID',
    'Task Name',
    'Custom Name',
    'Task Category',
    'Task Type',
    'Status',
    'Start Date/Time',
    'End Date/Time',
    'Duration (min)',
    'Duration (hrs)',
    'Downtime (min)',
    'Downtime Reason',
    'Net Duration (min)',
    'Labour (min)',
    'Labour (hrs)',
    'Team Size',
    'Workers',
    'Timer Type',
    'Room',
    'Product Name',
    'SKU',
    'Lot Number',
    'Units Completed',
    'Unit Type',
    'Units / Clock-Hour',
    'Units / Labour-Hour',
    `Wage Rate (${cur}/hr total crew)`,
    `Labour Cost (${cur})`,
    `Cost Per Unit (${cur})`,
    'Rate Source',
    'Tags',
    'Notes',
    'Submitted At',
  ];

  const rows = tasks.map((task) => {
    // Workers: individual names joined with semicolons, or "Team x N" for team-only tasks
    const workers = task.employeeNames?.length
      ? task.employeeNames.join('; ')
      : `Team x${task.teamSize || '?'}`;

    // Timer type: individual when specific floor workers were assigned
    const timerType = task.employeeIds?.length ? 'Individual' : 'Team';

    const durationHrs = task.durationMinutes != null
      ? (task.durationMinutes / 60).toFixed(3)
      : '';

    return [
      task.id,
      taskDisplayName(task),
      task.isCustomTaskName ? 'Yes' : 'No',
      task.taskCategoryName || '',
      task.taskType || '',
      task.status || '',
      csvDateTime(task.startTime),
      csvDateTime(task.endTime),
      task.durationMinutes ?? '',
      durationHrs,
      task.downtimeMinutes ?? '',
      task.downtimeReason || '',
      task.netDurationMinutes ?? '',
      task.labourMinutes ?? '',
      task.labourHours ?? '',
      task.teamSize ?? '',
      workers,
      timerType,
      task.roomName || '',
      task.productName || '',
      task.sku || '',
      task.lotNumber || '',
      task.quantityCompleted ?? '',
      task.unitType || '',
      task.unitsPerHour ?? '',
      task.unitsPerLabourHour ?? '',
      task.labourRateApplied ?? '',
      task.labourCost ?? '',
      task.costPerUnit ?? '',
      rateSourceLabel(task.labourRateSource),
      task.tagNames?.length ? task.tagNames.join('; ') : '',
      task.notes || '',
      csvDateTime(task.submittedAt),
    ];
  });

  return buildCSV(headers, rows);
}

/**
 * Worker performance summary — one row per named worker.
 * Labour cost is split equally among co-assigned workers (same method as the
 * dashboard's "Cost by Employee" chart). Team-only tasks (no individual names
 * assigned) appear as a "Team x N" row so the totals still balance.
 */
export function buildWorkerPerformanceCSV(tasks, currency) {
  const cur = currency || 'CAD';

  // Aggregate per worker name
  const map = {};

  for (const task of tasks) {
    const names = task.employeeNames?.length
      ? task.employeeNames
      : [`Team x${task.teamSize || '?'}`];
    const isTeamOnly = !task.employeeNames?.length;

    for (const name of names) {
      if (!map[name]) {
        map[name] = {
          name,
          isTeamOnly,
          tasks: 0,
          totalDurationMin: 0,
          totalLabourMin: 0,
          totalCost: 0,
          hasCost: false,
          totalUnits: 0,
          hasUnits: false,
          firstSeen: null,
          lastSeen: null,
        };
      }
      const w = map[name];
      w.tasks++;
      w.totalDurationMin += task.durationMinutes || 0;

      // Labour minutes: divide crew-total by headcount so each named worker
      // gets their individual share (e.g. 3 people × 60 min net = 180 crew-min,
      // each person's share = 60 min). For team-only rows names.length === 1
      // so the full crew total is preserved there.
      w.totalLabourMin += task.labourMinutes != null
        ? task.labourMinutes / names.length
        : 0;

      if (task.labourCost != null) {
        // Split cost equally across all workers on the task
        w.totalCost += task.labourCost / names.length;
        w.hasCost = true;
      }
      if (task.quantityCompleted != null) {
        w.totalUnits += task.quantityCompleted;
        w.hasUnits = true;
      }

      const ts = task.submittedAt?.toDate?.()
        ?? (task.submittedAt ? new Date(task.submittedAt) : null);
      if (ts && !isNaN(ts)) {
        if (!w.firstSeen || ts < w.firstSeen) w.firstSeen = ts;
        if (!w.lastSeen || ts > w.lastSeen) w.lastSeen = ts;
      }
    }
  }

  const headers = [
    'Worker Name',
    'Total Tasks',
    'Total Duration (min)',
    'Total Duration (hrs)',
    'Total Labour (min)',
    'Total Labour (hrs)',
    `Labour Cost (${cur})`,
    'Units Completed',
    'Avg Duration Per Task (min)',
    `Avg Cost Per Task (${cur})`,
    `Avg Cost Per Unit (${cur})`,
    'First Activity',
    'Last Activity',
    'Note',
  ];

  const rows = Object.values(map)
    .sort((a, b) => (b.totalCost - a.totalCost) || (b.tasks - a.tasks))
    .map((w) => {
      const avgDurMin = w.tasks > 0 ? Math.round(w.totalDurationMin / w.tasks) : '';
      const avgCostTask = w.hasCost && w.tasks > 0
        ? (w.totalCost / w.tasks).toFixed(2)
        : '';
      const avgCostUnit = w.hasCost && w.totalUnits > 0
        ? (w.totalCost / w.totalUnits).toFixed(4)
        : '';

      return [
        w.name,
        w.tasks,
        w.totalDurationMin,
        (w.totalDurationMin / 60).toFixed(3),
        w.totalLabourMin || '',
        w.totalLabourMin ? (w.totalLabourMin / 60).toFixed(3) : '',
        w.hasCost ? w.totalCost.toFixed(2) : '',
        w.hasUnits ? w.totalUnits : '',
        avgDurMin,
        avgCostTask,
        avgCostUnit,
        w.firstSeen ? csvDate(w.firstSeen) : '',
        w.lastSeen ? csvDate(w.lastSeen) : '',
        w.isTeamOnly ? 'Team-only task (no individual workers assigned)' : '',
      ];
    });

  return buildCSV(headers, rows);
}
